// Page editor (design §5, §10): frontmatter as form fields above a Markdown
// textarea with a Preview tab, a commit message, Save/Cancel. On a 409
// KB_CONFLICT the parent passes `conflict` and the editor shows your version
// next to the current one; you either keep yours (saved on top of the new
// rev) or take theirs.

import {
  useEffect,
  useId,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { kbAppliesToError } from '@goblic/shared'
import { Markdown } from '../markdown'
import { Field, Notice, Tabs } from '../ui'
import { KbDiff } from './kb-diff'
import { KbAuthorLine } from './kb-page-view'
import {
  serializeKbDocument,
  validateKbPath,
  type KbConflict,
  type KbFrontmatter,
  type KbPage,
  type KbSavePayload,
} from './kb-types'

export interface KbEditorProps {
  /** The page being edited; omit for a new page. */
  page?: KbPage | null
  /** Path of a new page (ignored when `page` is given). */
  newPath?: string
  /** Space label, shown before the path. */
  spaceLabel?: string
  /** Only people may set always_load (§5); hide the toggle otherwise. */
  canSetAlwaysLoad?: boolean
  saving?: boolean
  error?: string | null
  /** Set by the parent after a 409 KB_CONFLICT. */
  conflict?: KbConflict | null
  onDismissConflict?: () => void
  /** Extra actions in the conflict view (e.g. "Open the existing page"). */
  conflictActions?: ReactNode
  onSave: (payload: KbSavePayload) => void
  onCancel: () => void
}

interface Draft {
  title: string
  summary: string
  appliesTo: string[]
  tags: string[]
  alwaysLoad: boolean
  body: string
  /** Frontmatter fields the form doesn't edit, kept as-is. */
  rest: KbFrontmatter
}

function draftFrom(frontmatter: KbFrontmatter, body: string): Draft {
  // `deleted` belongs to the delete/restore flow, never to an edit.
  const { title, summary, applies_to, tags, always_load, deleted, ...rest } = frontmatter
  return {
    title: title ?? '',
    summary: summary ?? '',
    appliesTo: applies_to ?? [],
    tags: tags ?? [],
    alwaysLoad: always_load ?? false,
    body,
    rest,
  }
}

function documentOf(draft: Draft): string {
  return serializeKbDocument(
    {
      title: draft.title.trim() || undefined,
      summary: draft.summary.trim() || undefined,
      applies_to: draft.appliesTo,
      tags: draft.tags,
      always_load: draft.alwaysLoad,
      ...draft.rest,
    },
    draft.body
  )
}

const SUMMARY_MAX = 300

export function KbEditor({
  page,
  newPath,
  spaceLabel,
  canSetAlwaysLoad = true,
  saving,
  error,
  conflict,
  onDismissConflict,
  conflictActions,
  onSave,
  onCancel,
}: KbEditorProps) {
  const ids = { title: useId(), summary: useId(), message: useId(), body: useId() }
  const path = page?.path ?? newPath ?? ''
  const [draft, setDraft] = useState<Draft>(() =>
    page
      ? draftFrom(page.frontmatter, page.content)
      : draftFrom({}, '')
  )
  const [baseRev, setBaseRev] = useState<number | undefined>(page?.rev)
  const [message, setMessage] = useState('')
  const [tab, setTab] = useState<'write' | 'preview'>('write')
  const pathError = page ? null : validateKbPath(path)
  const appliesToError = kbAppliesToError(draft.appliesTo)

  // A different page was opened in the same editor instance.
  useEffect(() => {
    if (!page) return
    setDraft(draftFrom(page.frontmatter, page.content))
    setBaseRev(page.rev)
  }, [page?.space, page?.path]) // eslint-disable-line react-hooks/exhaustive-deps

  function update<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  function save(rev: number | undefined): void {
    onSave({
      path,
      content: documentOf(draft),
      message: message.trim() || undefined,
      baseRev: rev,
    })
  }

  function submit(event: FormEvent): void {
    event.preventDefault()
    if (pathError || appliesToError || saving) return
    save(baseRev)
  }

  function keepMine(): void {
    if (!conflict) return
    setBaseRev(conflict.currentRev)
    onDismissConflict?.()
    save(conflict.currentRev)
  }

  function takeTheirs(): void {
    if (!conflict) return
    if (conflict.current) setDraft(draftFrom(conflict.current.frontmatter, conflict.current.content))
    setBaseRev(conflict.currentRev)
    onDismissConflict?.()
  }

  if (conflict) {
    const theirs = conflict.current?.raw ?? ''
    const mine = documentOf(draft)
    return (
      <div className="space-y-4">
        <Notice tone="signal">
          <p className="font-medium">
            {page
              ? 'Someone else changed this page while you were editing.'
              : 'A page with this path already exists.'}
          </p>
          <p className="mt-0.5 text-sm text-ink-2">
            It is now at rev {conflict.currentRev}
            {conflict.current ? (
              <>
                {' '}
                (
                <KbAuthorLine
                  author={{
                    userId: conflict.current.updatedByUserId,
                    runId: conflict.current.updatedByRunId,
                  }}
                  at={conflict.current.updatedAt}
                />
                )
              </>
            ) : null}
            .{' '}
            {page
              ? 'Compare both versions, then keep yours or continue from theirs.'
              : 'Compare both versions: replace it with yours, continue from the existing one, or open it.'}
          </p>
        </Notice>
        <div className="grid gap-3 lg:grid-cols-2">
          <VersionPane label="Your version" text={mine} />
          <VersionPane label={`Current version (rev ${conflict.currentRev})`} text={theirs} />
        </div>
        <KbDiff
          before={theirs}
          after={mine}
          beforeLabel={`Current (rev ${conflict.currentRev})`}
          afterLabel="Yours"
        />
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Discard my edits
          </button>
          {conflictActions}
          <button type="button" className="btn-secondary" onClick={takeTheirs}>
            Continue from current version
          </button>
          <button type="button" className="btn-primary" onClick={keepMine} disabled={saving}>
            {saving ? 'Saving…' : 'Save my version'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-ink-3">
        {spaceLabel ? `${spaceLabel} / ` : null}
        <span className="font-mono text-ink-2">{path || '—'}</span>
        {baseRev != null ? <span> · editing rev {baseRev}</span> : <span> · new page</span>}
      </p>
      {pathError ? <Notice tone="error">{pathError}</Notice> : null}
      {error ? <Notice tone="error">{error}</Notice> : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title" htmlFor={ids.title} hint="Defaults to the first heading.">
          <input
            id={ids.title}
            className="field-input"
            value={draft.title}
            placeholder={page?.title}
            onChange={(event) => update('title', event.target.value)}
          />
        </Field>
        <Field
          label="Summary"
          htmlFor={ids.summary}
          hint={`${draft.summary.length}/${SUMMARY_MAX} · shown in the agents’ index.`}
        >
          <input
            id={ids.summary}
            className="field-input"
            maxLength={SUMMARY_MAX}
            value={draft.summary}
            onChange={(event) => update('summary', event.target.value)}
          />
        </Field>
        <Field
          label="Applies to"
          hint="Globs that link this page to code, e.g. packages/{api,web}/src/** — press Enter to add."
        >
          <ListInput
            values={draft.appliesTo}
            onChange={(values) => update('appliesTo', values)}
            placeholder="packages/web/src/**"
            mono
            braceAware
            validate={(item) => kbAppliesToError([item])}
          />
          {appliesToError ? (
            <p className="mt-1 text-sm text-danger" role="alert">
              {appliesToError}
            </p>
          ) : null}
        </Field>
        <Field label="Tags">
          <ListInput
            values={draft.tags}
            onChange={(values) => update('tags', values)}
            placeholder="api"
          />
        </Field>
      </div>

      {canSetAlwaysLoad ? (
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="switch mt-0.5"
            checked={draft.alwaysLoad}
            onChange={(event) => update('alwaysLoad', event.target.checked)}
          />
          <span>
            <span className="block text-base font-medium text-ink">Always load</span>
            <span className="block text-sm text-ink-3">
              Every run gets the full page in its prompt (budget: 4,000 tokens per space).
            </span>
          </span>
        </label>
      ) : null}

      <div>
        <Tabs
          label="Editor mode"
          value={tab}
          onChange={setTab}
          options={[
            { value: 'write', label: 'Write' },
            { value: 'preview', label: 'Preview' },
          ]}
        />
        {tab === 'write' ? (
          <textarea
            id={ids.body}
            aria-label="Page content (Markdown)"
            className="field-input mt-3 min-h-[24rem] font-mono text-sm leading-relaxed"
            value={draft.body}
            onChange={(event) => update('body', event.target.value)}
            spellCheck
          />
        ) : (
          <div className="panel mt-3 min-h-[24rem] p-5 text-base leading-relaxed">
            {draft.body.trim() ? (
              <Markdown>{draft.body}</Markdown>
            ) : (
              <p className="text-ink-3">Nothing to preview.</p>
            )}
          </div>
        )}
      </div>

      <Field label="Change note" htmlFor={ids.message} hint="Optional — why, like a commit message.">
        <input
          id={ids.message}
          className="field-input"
          value={message}
          placeholder="Describe the change"
          onChange={(event) => setMessage(event.target.value)}
        />
      </Field>

      <div className="flex justify-end gap-2">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={saving || Boolean(pathError) || Boolean(appliesToError)}>
          {saving ? 'Saving…' : page ? 'Save' : 'Create page'}
        </button>
      </div>
    </form>
  )
}

function VersionPane({ label, text }: { label: string; text: string }) {
  return (
    <div className="panel flex min-w-0 flex-col overflow-hidden">
      <header className="border-b border-line bg-sunken px-4 py-2 text-sm font-medium text-ink-2">
        {label}
      </header>
      <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap break-words p-3 font-mono text-xs leading-relaxed text-ink">
        {text}
      </pre>
    </div>
  )
}

/**
 * Splits on commas outside `{…}`, so `packages/{api,web}/**` stays one glob.
 * Without `braceAware` every comma splits.
 */
function splitItems(text: string, braceAware: boolean): string[] {
  if (!braceAware) return text.split(',')
  const items: string[] = []
  let depth = 0
  let current = ''
  for (const char of text) {
    if (char === '{') depth++
    else if (char === '}') depth = Math.max(0, depth - 1)
    if (char === ',' && depth === 0) {
      items.push(current)
      current = ''
    } else {
      current += char
    }
  }
  items.push(current)
  return items
}

/**
 * Chips plus an input; Enter (or, without `braceAware`, a comma) adds,
 * Backspace on empty removes the last. With `validate`, an invalid item
 * stays in the input with the message below it.
 */
function ListInput({
  values,
  onChange,
  placeholder,
  mono,
  braceAware = false,
  validate,
}: {
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  mono?: boolean
  /** Glob lists: a comma only separates items outside `{}`, and typing one never adds. */
  braceAware?: boolean
  validate?: (item: string) => string | null
}) {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)

  function add(): void {
    const items = splitItems(text, braceAware)
      .map((item) => item.trim())
      .filter((item) => item && !values.includes(item))
    if (validate) {
      for (const item of items) {
        const message = validate(item)
        if (message) {
          setError(`${item}: ${message}`)
          return
        }
      }
    }
    setError(null)
    if (items.length) onChange([...values, ...items])
    setText('')
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter' || (!braceAware && event.key === ',')) {
      event.preventDefault()
      add()
    } else if (event.key === 'Backspace' && !text && values.length) {
      onChange(values.slice(0, -1))
    }
  }

  return (
    <div>
      <div className="flex min-h-[2.125rem] flex-wrap items-center gap-1 rounded-control border border-line-strong bg-surface px-1.5 py-1 focus-within:border-moss focus-within:ring-2 focus-within:ring-moss/20">
        {values.map((value) => (
          <span
            key={value}
            className={`flex items-center gap-1 rounded bg-sunken px-1.5 py-0.5 text-sm text-ink ${mono ? 'font-mono text-xs' : ''}`}
          >
            {value}
            <button
              type="button"
              onClick={() => onChange(values.filter((v) => v !== value))}
              className="text-ink-3 hover:text-danger"
              aria-label={`Remove ${value}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className={`min-w-[8rem] flex-1 border-0 bg-transparent p-0.5 text-base text-ink placeholder:text-ink-3 focus:outline-none focus:ring-0 ${mono ? 'font-mono text-sm' : ''}`}
          value={text}
          placeholder={values.length ? '' : placeholder}
          aria-invalid={error ? true : undefined}
          onChange={(event) => {
            setText(event.target.value)
            if (error) setError(null)
          }}
          onKeyDown={onKeyDown}
          onBlur={add}
        />
      </div>
      {error ? (
        <p className="mt-1 text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
